import { Colors } from "../../utils/colors";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../Containers/PopupModalContainer";
import RectangleButton from "../Buttons/RectangleButton";
import { useCallback, useMemo, useState } from "react";
import NumberInput from "../NumberInput";
import { API } from "../../API";
import { getAccountState } from "../../redux/slices/accountSlice";
import { useSelector } from "react-redux";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import { FormatCurrency } from "../../utils/FormatCurrency";
import {
  STRIPE_BASE_FIXED_FEE,
  STRIPE_BASE_RECURRING_PERCENT_FEE,
  stripeFlatFeeAmount,
} from "@markit/common.utils";
import { testStripe } from "../../utils/stripeAccountUtils";

type AddStripeFundsModalProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  defaultDepositAmount?: number;
};

const AddStripeFundsModal = (props: AddStripeFundsModalProps) => {
  const { isVisible, setIsVisible, defaultDepositAmount } = props;
  const { uid } = useSelector(getAccountState).account.accountData;
  const [depositAmount, setDepositAmount] = useState<number | undefined>(
    defaultDepositAmount || 0
  );
  const [loadingAddFunds, setLoadingAddFunds] = useState(false);

  const styles = {
    inputContainer: { padding: "10px 14px", borderRadius: 12, width: 100 },
  };

  // deposit amount + stripe billing fee (in cents)
  const finalDepositAmountCents = useMemo(() => {
    const depositAmountCents = (depositAmount || 0) * 100;
    const amount = depositAmountCents
      ? depositAmountCents + stripeFlatFeeAmount(depositAmountCents, true)
      : 0;
    return Math.round(amount);
  }, [depositAmount]);

  const addFundsToBalance = useCallback(async () => {
    try {
      setLoadingAddFunds(true);

      const response = await (
        testStripe(uid)
          ? API.testPayment.createStripeInvoiceLink
          : API.payment.createStripeInvoiceLink
      )({
        userId: uid,
        amount: (depositAmount || 0) * 100,
      });

      const { invoiceUrl } = response;
      if (invoiceUrl) {
        window.open(invoiceUrl, "_blank");
      } else {
        alert("Failed to create payment link. Please try again.");
      }
    } catch (error: any) {
      alert("Failed to create payment link. Please try again.");
    } finally {
      // Close out after three seconds
      setTimeout(() => {
        setLoadingAddFunds(false);
        setIsVisible(false);
      }, 3000);
    }
  }, [uid, depositAmount, setIsVisible]);

  return (
    <PopupModalContainer
      headerComp={
        <div className="ColumnNormal" style={{ gap: 4 }}>
          <span className="sectionTitle">Add Funds</span>
          <span className="smallBodySubtext" style={{ fontWeight: 400 }}>
            Add funds directly to your Stripe balance. You will be redirected to
            Stripe to complete the transaction.
          </span>
        </div>
      }
      altPadding={0}
      valueComp={
        <div className="ColumnNormal">
          <div
            className="AlignedRowSpaced"
            style={{ gap: 24, paddingInline: 14 }}
          >
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <span className="bodyMedium">Deposit Amount</span>
              <span className="smallBodySubtext">
                Specify an exact dollar amount to deposit into your Stripe
                balance.
              </span>
            </div>
            <NumberInput
              min={1}
              max={1000}
              prefix={"$"}
              value={depositAmount}
              onChange={(value) => {
                const roundedValue = Number(value?.toFixed(2));
                setDepositAmount(roundedValue);
              }}
              containerStyles={styles.inputContainer}
              allowDecimal
            />
          </div>
          <HorizontalDivider altMargin={14} />
          <div className="ColumnNormal" style={{ paddingInline: 14, gap: 7 }}>
            <div className="AlignedRowSpaced">
              <span className="bodyMedium">Total Deposit Amount</span>
              <span className="bodyMedium">
                {FormatCurrency(finalDepositAmountCents / 100)}
              </span>
            </div>
            <span className="smallBodySubtext">
              When you deposit funds into Stripe, you are subject to a{" "}
              {STRIPE_BASE_RECURRING_PERCENT_FEE * 100}% + $
              {(STRIPE_BASE_FIXED_FEE / 100).toFixed(2)} fee. We automatically
              add this onto your deposit amount..
            </span>
          </div>
        </div>
      }
      fixedBottomContent={
        <div className="ColumnNormal">
          <HorizontalDivider altMargin={14} />
          <div className="RowNormal" style={{ gap: 14, paddingInline: 14 }}>
            <RectangleButton
              buttonLabel="Cancel"
              onPress={() => setIsVisible(false)}
              altTextColor={Colors.BLACK}
              altColor={Colors.WHITE1}
              containerStyles={{
                padding: "14px 12px",
                border: `0.5px solid ${Colors.GRAY2}`,
              }}
            />
            <RectangleButton
              buttonLabel={"Add Funds"}
              onPress={addFundsToBalance}
              altTextColor={Colors.WHITE}
              altColor={Colors.PURPLE11}
              containerStyles={{ padding: "14px 12px" }}
              loading={loadingAddFunds}
              disabled={
                (depositAmount &&
                  (depositAmount < 1 || depositAmount > 1000)) ||
                !depositAmount
              }
            />
          </div>
          <div
            className="ColumnNormal"
            style={{ paddingInline: 14, paddingTop: 14 }}
          >
            <span className="bodySmallRegular">
              Note: After adding funds to your account, refresh your Stripe
              balance to view your updated account balance.
            </span>
          </div>
        </div>
      }
      noExit
      closeModal={() => setIsVisible(false)}
      hideModal={!isVisible}
      modalContentWidth={PopupModalContainerWidth.SMALL}
    />
  );
};

export default AddStripeFundsModal;
